import React, { useState, useEffect } from "react";
import { getDocument, PDFDocumentProxy } from "pdfjs-dist";

interface FileUploaderProps {
  onFilesUpload: (files: File[]) => void;
  loading?: boolean;
}

const FileUploader: React.FC<FileUploaderProps> = ({
  onFilesUpload,
  loading,
}) => {
  const [selectedFiles, setSelectedFiles] = useState<FileList | null>(null);
  const [previews, setPreviews] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSelectedFiles(event.target.files);
  };

  const handleDrop = (e: React.DragEvent<HTMLLabelElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (loading) return;
    setIsDragging(false);
    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      setSelectedFiles(files);
    }
  };

  const handleDragOver = (e: React.DragEvent<HTMLLabelElement>) => {
    e.preventDefault();
    if (loading) return;
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleUpload = async () => {
    if (!selectedFiles || selectedFiles.length === 0) {
      setError("Please select at least one file.");
      return;
    }
    setError(null);
    onFilesUpload(Array.from(selectedFiles));
  };

  // Generate thumbnails when files are selected
  useEffect(() => {
    const generatePreviews = async () => {
      if (!selectedFiles) return;
      const fileArray = Array.from(selectedFiles);
      const newPreviews: string[] = [];

      for (const file of fileArray) {
        if (file.type === "application/pdf") {
          try {
            const arrayBuffer = await file.arrayBuffer();
            const loadingTask = getDocument({ data: arrayBuffer });
            const pdf: PDFDocumentProxy = await loadingTask.promise;

            const page = await pdf.getPage(1);
            const viewport = page.getViewport({ scale: 1.5 });

            const canvas = document.createElement("canvas");
            const context = canvas.getContext("2d");

            canvas.width = viewport.width;
            canvas.height = viewport.height;

            await page.render({
              canvas, // ✅ Required
              canvasContext: context!,
              viewport,
            }).promise;

            newPreviews.push(canvas.toDataURL());
          } catch (err) {
            console.error("PDF preview error:", err);
            newPreviews.push("");
          }
        } else if (file.type.startsWith("image/")) {
          newPreviews.push(URL.createObjectURL(file));
        } else {
          newPreviews.push("");
        }
      }

      setPreviews(newPreviews);
    };

    generatePreviews();
  }, [selectedFiles]);

  return (
    <div className="p-6 rounded-2xl bg-[#1e293b] text-white w-full max-w-xl mx-auto mt-10">
      <h2 className="text-xl font-semibold mb-4 text-[#d0e4ff] text-center">
        Upload Menu (PDF or Image)
      </h2>

      <label
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        className={`block w-full p-6 border-2 border-dashed rounded-xl text-center transition duration-200 cursor-pointer
          ${loading ? "opacity-50 cursor-not-allowed" : ""}
          ${isDragging ? "border-[#4c6fa9] bg-[#24384f]" : "border-[#2f4158] hover:border-[#3f5a78]"}`}
      >
        <input
          type="file"
          multiple
          accept=".pdf,image/*"
          disabled={loading}
          onChange={handleFileChange}
          className="hidden"
        />
        <span className="text-[#9db4d3]">
          {loading
            ? "⏳ Processing..."
            : isDragging
            ? "Drop files here..."
            : "Click or drag files here to upload"}
        </span>
      </label>

      {selectedFiles && (
        <div className="mt-4 grid grid-cols-2 gap-4">
          {Array.from(selectedFiles).map((file, i) => (
            <div
              key={i}
              className="flex flex-col items-center bg-[#24384f] p-2 rounded-lg"
            >
              {previews[i] ? (
                <img
                  src={previews[i]}
                  alt={file.name}
                  className="w-24 h-24 object-cover rounded mb-2"
                />
              ) : (
                <div className="w-24 h-24 bg-[#334155] flex items-center justify-center text-xs text-gray-400 rounded mb-2">
                  No Preview
                </div>
              )}
              <span className="text-xs truncate w-24">{file.name}</span>
            </div>
          ))}
        </div>
      )}

      <button
        onClick={handleUpload}
        disabled={loading}
        className={`w-full mt-6 py-2 rounded-lg font-semibold transition ${
          loading
            ? "bg-[#2c3e50] text-gray-400 cursor-not-allowed"
            : "bg-[#37597e] hover:bg-[#2f4d6d] text-white"
        }`}
      >
        {loading ? "Processing..." : "Upload & Convert"}
      </button>

      {error && <p className="text-red-400 mt-2 text-center">{error}</p>}
    </div>
  );
};

export default FileUploader;
